import { useState, useMemo, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Row,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

import { H3, MUIIcons } from "../../../AbstractElements";
import { SURVEY_CREATION_USERS_STATUS } from "../../../Api/constants";
import FilterSliderModal from "../../../CommonElements/FilterSliderModal";
import CommonModal from "../../../CommonElements/Modal";
import SearchBar from "../../../CommonElements/SearchBar";
import { useAppSelector, useAppDispatch } from "../../../ReduxToolkit/Hooks";
import {
  getSurveyList,
  reviewSurvey,
} from "../../../ReduxToolkit/Reducers/SurveySlice";
import { getUsersList } from "../../../ReduxToolkit/Reducers/UserSlice";
import {
  Description,
  CreatedBy,
  CreatedOn,
  NoOfQuestions,
  Action,
  Approve,
  Reject,
  Confirm,
  SideBarTitlePendingForApproval,
  ApproveSurveyConfirmation,
  RejectSurveyConfirmation,
  Search,
} from "../../../utils/Constant";
import { formatCustomDate } from "../../../utils/helper/helper";
import "../ManageSurveys/style.scss";

const PendingApproval = () => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [defaultFilterValues, setDefaultFilterValues] = useState({});
  const [clearData, setClearData] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const dispatch = useAppDispatch();
  const { usersList } = useAppSelector(state => state.user);
  const { surveyList } = useAppSelector(state => state.survey);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getUsersList({ role: SURVEY_CREATION_USERS_STATUS }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getSurveyList({ status: "pending_approval" }));
  }, [dispatch]);

  const userMap = useMemo(() => {
    const map = {};
    usersList?.users?.forEach(user => {
      map[user.id] = user;
    });
    return map;
  }, [usersList]);

  const createdByOptions = useMemo(() => {
    if (!usersList) return [];
    return usersList?.users?.map(user => ({
      name: String(user.id),
      label: user.user_name,
      value: user.user_name,
    }));
  }, [usersList]);

  const handleSearch = value => {
    setSearchValue(value);
    setClearData(false);
  };

  const handleDone = (values = {}) => {
    setClearData(true);
    dispatch(
      getSurveyList({
        status: "pending_approval",
        owner_id: values["created_by"]?.toString() || "",
      })
    );
  };
  const handleClear = () => {
    setClearData(true);
    setSelectedFilterValues({});
    setDefaultFilterValues({});
    dispatch(getSurveyList({ status: "pending_approval" }));
  };

  const toggleDropdown = rowId => {
    setDropdownOpen(prev => (prev === rowId ? null : rowId));
  };

  const handleActionClick = (row, type) => {
    setSelectedSurvey(row);
    setModalType(type);
    setIsModalOpen(true);
    setReason("");
    setReasonError("");
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalType("");
    setSelectedSurvey(null);
    setReason("");
    setReasonError("");
  };

  const handleModalConfirm = async () => {
    if (!selectedSurvey) return;
    if (modalType === "approve") {
      await dispatch(
        reviewSurvey({
          survey_id: selectedSurvey.id,
          status: "approved",
          feedback: null,
        })
      );
      handleModalClose();
      dispatch(getSurveyList({ status: "pending_approval" }));
    } else if (modalType === "reject") {
      if (!reason.trim()) {
        setReasonError("Reason is required");
        return;
      }
      await dispatch(
        reviewSurvey({
          survey_id: selectedSurvey.id,
          status: "rejected",
          feedback: reason,
        })
      );
      handleModalClose();
      dispatch(getSurveyList({ status: "pending_approval" }));
    }
  };

  const filteredData = useMemo(() => {
    let rows = surveyList?.surveys || [];
    if (searchValue) {
      rows = rows.filter(row =>
        Object.values(row)
          .join(" ")
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );
    }
    return rows;
  }, [surveyList, searchValue]);

  const columns = [
    {
      name: "Title",
      selector: row => row.title,
      sortable: true,
      minWidth: "15rem",
    },
    {
      name: Description,
      selector: row => row.description,
      sortable: true,
      minWidth: "16rem",
    },
    {
      name: CreatedBy,
      selector: row => userMap[row.created_by]?.user_name || "-",
      sortable: true,
      minWidth: "14rem",
    },
    {
      name: CreatedOn,
      selector: row => formatCustomDate(row.created_at),
      sortable: true,
      minWidth: "10rem",
    },
    {
      name: NoOfQuestions,
      selector: row => row.no_of_questions,
      sortable: true,
      minWidth: "8rem",
    },
    {
      name: Action,
      cell: row => (
        <Dropdown
          isOpen={dropdownOpen === row.id}
          toggle={() => toggleDropdown(row.id)}
          direction="start"
        >
          <DropdownToggle color="" className="three-dots primary-text-color">
            <MUIIcons
              iconName="MoreVertOutlined"
              className="primary-icon-color"
              size={17}
            />
          </DropdownToggle>
          <DropdownMenu className="dropdown-menu-end" container={document.body}>
            <DropdownItem onClick={() => handleActionClick(row, "approve")}>
              {Approve}
            </DropdownItem>
            <DropdownItem onClick={() => handleActionClick(row, "reject")}>
              {Reject}
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "10rem",
      className: "fixed-action-column",
    },
  ];

  return (
    <div className="page-body page-body-margin assessment-catlog-list catlog-assessments pb-5">
      <Card className="p-1">
        <CardBody>
          <Row className="d-flex flex-column flex-md-row justify-content-between align-items-center">
            <Col
              lg="6"
              md="12"
              sm="12"
              xs="12"
              className="mb-1 mb-sm-0 d-flex flex-column gap-2 "
            >
              <H3>{SideBarTitlePendingForApproval}</H3>
            </Col>
            <Col
              lg="6"
              md="12"
              sm="12"
              xs="12"
              className="d-flex flex-wrap justify-content-end align-items-center gap-3 ms-auto"
            >
              <div className="w-100 d-block d-sm-none mb-2">
                <SearchBar
                  showSearch
                  onSearch={handleSearch}
                  clearOnProp={clearData}
                  placeholder={Search}
                />
              </div>
              <div className="d-none d-sm-block">
                <SearchBar
                  showSearch
                  onSearch={handleSearch}
                  clearOnProp={clearData}
                  placeholder={Search}
                />
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <div className="mt-1">
                  <FilterSliderModal
                    dropdowns={[
                      {
                        label: "Created By",
                        key: "created_by",
                        tooltipText: "Select Created By",
                        options: createdByOptions,
                        isMultiSelect: true,
                      },
                    ]}
                    selectedFilterValues={selectedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    setSelectedFilterValues={setSelectedFilterValues}
                    setDefaultFilterValues={setDefaultFilterValues}
                    onDone={handleDone}
                    onClear={handleClear}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
      <Card className="mt-3">
        <CardBody>
          <DataTable
            className="pending-approval-table-wrapper__table"
            columns={columns}
            data={filteredData}
            pagination
            highlightOnHover
            pointerOnHover
            responsive
            noHeader
            onRowClicked={row => navigate(`/survey-preview?id=${row.id}`)}
          />
        </CardBody>
      </Card>
      <CommonModal
        isOpen={isModalOpen}
        toggle={handleModalClose}
        showFooter
        primaryBtnText={Confirm}
        secondaryBtnText={"Cancel"}
        onPrimaryBtnClick={handleModalConfirm}
        onSecondaryBtnClick={handleModalClose}
        title={modalType === "approve" ? Approve : Reject}
      >
        {modalType === "approve" ? (
          <div>{ApproveSurveyConfirmation}</div>
        ) : (
          <div>
            <div>{RejectSurveyConfirmation}</div>
            <textarea
              className="form-control mt-2"
              rows={3}
              placeholder="Enter reason (required)"
              value={reason}
              onChange={e => {
                setReason(e.target.value);
                setReasonError("");
              }}
              maxLength={255}
            />
            {reasonError && (
              <div className="text-danger mt-1">{reasonError}</div>
            )}
          </div>
        )}
      </CommonModal>
    </div>
  );
};

export default PendingApproval;
